import React, { useEffect, useRef, useState } from 'react'
import './ImageSelectorModal.scss'
import Icon from '../Icon'
import ImageItem from './ImageItem'
import UploadItem from './UploadItem'
import { replaceMessageKeys, uploadAsset } from './uploadUtils'
import { prependServerDomain } from '../../utils/prependServerDomain'

const Search = ({ onChange, value, t }) => {
  return (
    <div className="xmp-gs-sm-search">
      <Icon name="search.svg" width="16px" height="16px"/>
      <input type="text" placeholder={t('GallerySelectorDuc.Search')} onChange={onChange} value={value}/>
      {value && <Icon name="closeBig.svg" width="14px" height="14px" onClick={() => onChange({ target: { value: '' } })}
                      className="reset-gallery-search"/>}
    </div>
  )
}

const List = ({
  title,
  list,
  showNoResults,
  onDelete,
  onSelect,
  onUpload,
  uiSchema,
  allowUpload,
  t,
  scrollableRef,
  uploadErrors,
  validation,
  isUploading,
  value,
  setClearUpload,
  defaultAssets = [],
  UStoreProvider,
}) => {
  const allowedExtensions = uiSchema['ui:options'].custom.allowedExtensions
  if (!allowUpload && list.length === 0 && defaultAssets.length === 0 && !showNoResults) {
    return null
  }
  return (
    <div className="xmp-gs-sm-list">
      <div className="xmp-gs-sm-list-title">
        {title}
      </div>
      <div className="xmp-gs-sm-list-items">
        {allowUpload && <UploadItem {...{ allowedExtensions, onUpload, uploadErrors, isUploading, t, validation, setClearUpload, UStoreProvider }}/>}
        {list.map(item => <ImageItem {...{ item, onDelete, onSelect, t, scrollableRef, validation, uiSchema, value, setClearUpload, defaultAssets, UStoreProvider }}/>)}
      </div>
      {showNoResults && list.length === 0 &&
        <div className="xmp-gs-sm-list-no-results">{t('GallerySelectorDuc.NoResultFound')}</div>}
    </div>
  )
}

export const ImageSelectorModal = ({ onClose, onSelect, uiSchema, t, id, validation, selectedItem, onClearSelection, value, setClearUpload, defaultAssets = [], UStoreProvider }) => {
  const scrollableRef = useRef(null)
  const [uploadErrors, setUploadErrors] = useState([])
  const [assetList, setAssetList] = useState([])
  const [myImagesList, setMyImagesList] = useState([])
  const [searchResults, setSearchResults] = useState([])
  const [searchValue, setSearchValue] = useState('')
  const [isUploading, setIsUploading] = useState(false)
  const allowUpload = uiSchema['ui:options'].custom.allowUpload

  useEffect(() => {
    (async () => {
      const images = await UStoreProvider.api.assets.getAssets(id)
      const imageList = images.map(image => ({ ...image, ThumbnailUrl: prependServerDomain(image.ThumbnailUrl, UStoreProvider)}))
      const sortedImageList = imageList.sort((a, b) =>
        Date.parse(b.ModificationDate) - Date.parse(a.ModificationDate)
      )
      setAssetList(sortedImageList.filter(image => !image.IsUploaded))
      setMyImagesList(sortedImageList.filter(image => image.IsUploaded))
    })()
  }, [])

  const filterImageName = (name, value) => {
    const regex = new RegExp(value, 'i')
    return regex.test(name)
  }

  const onSearchChange = (e) => {
    const value = e.target.value
    setSearchValue(value)
    if (value) {
      setSearchResults([...myImagesList.filter(image => filterImageName(image.DisplayName, value.trim())), ...assetList.filter(image => filterImageName(image.DisplayName, value.trim()))])
    }
  }

  const onDelete = async (item) => {
    await UStoreProvider.api.assets.deleteAsset(item.AssetID)
    if (item.AssetID === selectedItem?.AssetID) {
      onClearSelection()
    }
    const images = await UStoreProvider.api.assets.getAssets(id)
    const imageList = images.map(image => ({ ...image, ThumbnailUrl: prependServerDomain(image.ThumbnailUrl, UStoreProvider)}))
    setMyImagesList(imageList.filter(image => image.IsUploaded))
  }

  const onSelectHandle = (item) => onSelect(item)

  const onUploadHandle = async ({ file, imageData }) => {
    setIsUploading(true)
    uploadAsset(id, file, imageData, validation).then(({ errors, item }) => {
      setIsUploading(false)
      if (errors.length > 0) {
        const maxSize = validation.find(v => v.validationType === 'ImageMaxSize')
        if (maxSize) {
          if (errors.find(e => e === maxSize.messageKey)) {
            setUploadErrors(replaceMessageKeys(errors, uiSchema))
            return
          }
        }
        setUploadErrors(replaceMessageKeys(errors, uiSchema))
        onSelect(item)
      } else {
        setUploadErrors([])
        onSelect(item)
      }
      const updatedList = UStoreProvider.state.get().assets
      setMyImagesList(updatedList.filter(image => image.IsUploaded))
    })
  }

  return (
    <div className="xmp-gs-sm">
      <div className="xmp-gs-sm-backdrop"/>
      <div className="xmp-gs-sm-modal">
        <div className="xmp-gs-sm-header">
          <div className="xmp-gs-sm-title">{t('GallerySelectorDuc.SelectImage')}</div>
          <div className="xmp-gs-sm-close">
            <Icon name="closeBig.svg" width="20px" height="20px" onClick={onClose}/>
          </div>
        </div>
        <div className="xmp-gs-sm-body-search-container">
          <Search onChange={onSearchChange} value={searchValue} t={t}/>
        </div>
        <div className="xmp-gs-sm-body" ref={scrollableRef}>
          {searchValue ?
            <List {...{
              title: t('GallerySelectorDuc.Results', { count: searchResults.length }),
              list: searchResults,
              showNoResults: true,
              onSelect: onSelectHandle,
              onDelete, t, scrollableRef, validation, uiSchema, value, setClearUpload, defaultAssets,UStoreProvider
            }}/> :
            <>
              <List
                {...{
                  title: t('GallerySelectorDuc.MyImages'),
                  list: myImagesList,
                  onSelect: onSelectHandle,
                  onUpload: onUploadHandle,
                  onDelete, uploadErrors, isUploading, uiSchema, allowUpload, t,
                  scrollableRef, validation, value, setClearUpload, UStoreProvider
                }}/>
              {(assetList.length > 0 || defaultAssets.length > 0) &&
                <List {...{
                  title: t('GallerySelectorDuc.ImageLibrary'),
                  list: [...defaultAssets, ...assetList],
                  onSelect: onSelectHandle, uiSchema, t, scrollableRef, validation, value,
                  setClearUpload, defaultAssets, UStoreProvider
                }}/>}
            </>}
        </div>
      </div>
    </div>
  )
}
